import { createServerFn } from "@tanstack/react-start";
import { getCookie, setCookie } from "@tanstack/react-start/server";
import { createContext, useContext, useState } from "react";
import z from "zod";
import type { QuickFilter } from "@/hooks/use-chart-filters";
import { Route } from "@/routes/__root";

// Represents the user preferences that can be stored in cookies
const PreferencesSchema = z
	.object({
		// Quick filter for stats, must be one of the predefined values
		statsQuickFilter: z
			.unknown()
			.transform((value) =>
				typeof value === "string" &&
				(
					[
						"all",
						"today",
						"7d",
						"30d",
						"365d",
						"m",
						"y",
						"-m",
						"-y",
						"custom",
					] satisfies Array<QuickFilter>
				).includes(value)
					? value
					: "7d",
			)
			.default("7d"),
	})
	.strip();

type Preferences = z.infer<typeof PreferencesSchema>;

const preferencesCookieName = "backoffice-preferences";

type PreferencesProviderProps = {
	children: React.ReactNode;
};

type PreferencesProviderState = {
	preferences: Preferences;
	setPreferences: (preferences: Partial<Preferences>) => void;
};

const initialState: PreferencesProviderState = {
	preferences: PreferencesSchema.parse({}),
	setPreferences: () => null,
};

const PreferencesProviderContext = createContext<PreferencesProviderState>(initialState);

/**
 * Server function to get the current preferences from cookies.
 * Returns the preferences object or a default value if not set.
 */
export const getPreferencesServerFn = createServerFn().handler((): Preferences => {
	const cookiePreferences = getCookie(preferencesCookieName);

	const preferences = PreferencesSchema.parse(
		cookiePreferences ? JSON.parse(cookiePreferences) : {},
	);

	if (preferences instanceof z.ZodError) {
		return PreferencesSchema.parse({});
	}

	return preferences;
});

export const setPreferencesServerFn = createServerFn({ method: "POST" })
	.inputValidator(PreferencesSchema)
	.handler(async ({ data }) => {
		return setCookie(preferencesCookieName, JSON.stringify(PreferencesSchema.parse(data)));
	});

export function PreferencesProvider({ children, ...props }: PreferencesProviderProps) {
	const { preferences: initialPreferences } = Route.useLoaderData();

	// Use local state to manage preferences on the client side
	const [preferences, setPreferencesState] = useState<Preferences>(initialPreferences);

	function setPreferences(newPreferences: Partial<Preferences>) {
		setPreferencesState((currentPreferences) => ({
			...currentPreferences,
			...newPreferences,
		}));

		setPreferencesServerFn({
			data: { ...preferences, ...newPreferences },
		});
	}

	const value = {
		preferences,
		setPreferences,
	};

	return (
		<PreferencesProviderContext.Provider {...props} value={value}>
			{children}
		</PreferencesProviderContext.Provider>
	);
}

export const usePreferences = () => {
	const context = useContext(PreferencesProviderContext);

	if (context === undefined)
		throw new Error("usePreferences must be used within a PreferencesProvider");

	return context;
};
